import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "requests-view" }
const _hoisted_2 = { class: "d-flex align-center ga-2" }
const _hoisted_3 = { class: "text-body-1 mb-4" }
const _hoisted_4 = { key: 0 }
const _hoisted_5 = { class: "text-body-1 mb-4" }
const _hoisted_6 = { class: "text-h6 font-weight-bold" }
const _hoisted_7 = { key: 1 }
const _hoisted_8 = { class: "text-body-1 mb-4" }
const _hoisted_9 = {
  key: 1,
  class: "text-grey"
}
const _hoisted_10 = { class: "text-body-1 mb-4" }

import { ref, computed, onMounted } from 'vue'
import { api } from '@/composables/api'
import { useAuthStore } from '@/store/auth'
import RequestsStatsCards from '@/components/requests/RequestsStatsCards.vue'
import RequestsFilters from '@/components/requests/RequestsFilters.vue'
import RequestsTable from '@/components/requests/RequestsTable.vue'
import RequestDetailsDialog from '@/components/requests/RequestDetailsDialog.vue'
import EquipmentSelectionModal from '@/components/requests/EquipmentSelectionModal.vue'
import BulkEditRequestModal from '@/components/requests/BulkEditRequestModal.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'RequestsView',
  setup(__props) {

const authStore = useAuthStore()
const loading = ref(true)
const requests = ref<any[]>([])
const stats = ref<any>({})
const searchQuery = ref('')
const filterStatus = ref<string[]>(['pending', 'filled'])
const filterSchool = ref('')
const selectedRequest = ref<any>(null)
const showFillModal = ref(false)
const requestToFill = ref<any>(null)
const loadingEquipment = ref(false)
const availableEquipment = ref<any[]>([])
const showEditModal = ref(false)
const requestToEdit = ref<any>(null)
const editFormRef = ref<any>(null)
const submittingEdit = ref(false)
const showClaimModal = ref(false)
const equipmentToClaim = ref<any>(null)
const schools = ref<any[]>([])
const showDeliveredModal = ref(false)
const requestsToDeliver = ref<any[]>([])
const showDeleteModal = ref(false)
const requestToDelete = ref<any>(null)
const deletingRequest = ref(false)
const selectedRequests = ref<string[]>([])
const showBulkEditModal = ref(false)
const bulkEditRequestIds = ref<string[]>([])
const bulkEditSubmitting = ref(false)
const bulkEditError = ref('')
const users = ref<any[]>([])
const sendingNotification = ref(false)

// Snackbar state
const showSnackbar = ref(false)
const snackbarMessage = ref('')
const snackbarColor = ref('success')

// Edit form data
const editFormData = ref({
  student_name: '',
  school: '',
  equipment_type: '',
  status: '',
  needed_for: [] as string[],
  minimum_rating: 1,
  assignee: '',
  teacher_email: '',
  notes: ''
})

// Dropdown options
const schoolOptions = computed(() => schools.value.map(s => ({
  title: s.name,
  value: s.id || s._id
})))
const equipmentTypeOptions = [
  { title: 'Laptop', value: 'laptop' },
  { title: 'Desktop', value: 'desktop' },
  { title: 'Monitor', value: 'monitor' },
  { title: 'Tablet', value: 'tablet' },
  { title: 'Other', value: 'other' }
]
const statusOptions = [
  { title: 'Pending', value: 'pending' },
  { title: 'Filled', value: 'filled' },
  { title: 'Completed', value: 'completed' },
  { title: 'Denied', value: 'denied' }
]
const taskOptions = [
  { title: 'Email', value: 'email' },
  { title: 'Web Browsing', value: 'web_browsing' },
  { title: 'Office Work', value: 'office_work' },
  { title: 'Document Editing', value: 'document_editing' },
  { title: 'Video Conferencing', value: 'video_conferencing' },
  { title: 'Presentations', value: 'presentations' },
  { title: 'Programming/Coding', value: 'coding' },
  { title: 'Photo Editing', value: 'photo_editing' },
  { title: 'Video Editing', value: 'video_editing' },
  { title: '3D Modeling', value: '3d_modeling' },
  { title: 'Gaming', value: 'gaming' },
  { title: 'Creative Work', value: 'creative_work' },
  { title: 'Machine Learning', value: 'machine_learning' },
  { title: 'CAD', value: 'cad' },
  { title: 'Music Production', value: 'music_production' },
  { title: 'Animation', value: 'animation' }
]

const isAdmin = computed(() => authStore.authUser && authStore.authUser.isAdmin())
const userSchool = computed(() => authStore.authUser?.school || null)

// User options for assignee dropdown - only users with a school
const userOptions = computed(() => {
  return users.value
    .filter(user => user.school) // Only users with a school
    .map(user => ({
      title: `${user.first_name || ''} ${user.last_name || ''} (${user.email})`.trim(),
      value: user.id
    }))
})

// School filter options
const schoolFilterOptions = computed(() => {
  const options = [{ title: 'All Schools', value: '' }]
  schools.value.forEach(school => {
    options.push({ title: school.name, value: school.id || school._id })
  })
  return options
})

// Notification helper
const showNotification = (message: string, color: string = 'success') => {
  snackbarMessage.value = message
  snackbarColor.value = color
  showSnackbar.value = true
}

const filteredRequests = computed(() => {
  let filtered = requests.value

  // Filter by school
  if (filterSchool.value) {
    filtered = filtered.filter(r => r.school === filterSchool.value)
  }

  // Filter by status
  if (filterStatus.value && filterStatus.value.length > 0) {
    filtered = filtered.filter(r => filterStatus.value.includes(r.status))
  }

  // Search by student name or school
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(r =>
      (r.student_name?.toLowerCase() || '').includes(query) ||
      (r.school?.toLowerCase() || '').includes(query)
    )
  }

  // Sort by submitted date (oldest first - FIFO)
  filtered.sort((a, b) => {
    const dateA = new Date(a.submitted_at || 0).getTime()
    const dateB = new Date(b.submitted_at || 0).getTime()
    return dateA - dateB
  })

  return filtered
})

const loadRequests = async () => {
  loading.value = true
  try {
    const response = await api.get('/requests')
    if (response.data) {
      requests.value = response.data
    }
  } catch (error) {
    console.error('Error loading requests:', error)
  } finally {
    loading.value = false
  }
}

const loadStats = async () => {
  try {
    const response = await api.get('/requests/stats')
    if (response.data) {
      stats.value = response.data
    }
  } catch (error) {
    console.error('Error loading stats:', error)
  }
}

const loadSchools = async () => {
  try {
    const response = await api.get('/schools')
    schools.value = response.data || response || []
  } catch (error) {
    console.error('Error loading schools:', error)
    schools.value = []
  }
}

const loadUsers = async () => {
  try {
    const response = await api.get('/users')
    users.value = response.data || response || []
  } catch (error) {
    console.error('Error loading users:', error)
    users.value = []
  }
}

const fulfillRequest = async (request: any) => {
  // This function is no longer used - kept for compatibility
  try {
    await api.patch(`/requests/${request.id}/fulfill`)
    await loadRequests()
    await loadStats()
    if (selectedRequest.value && selectedRequest.value.id === request.id) {
      selectedRequest.value = null
    }
    showNotification('Request marked as fulfilled', 'success')
  } catch (error) {
    console.error('Error fulfilling request:', error)
    showNotification('Failed to fulfill request', 'error')
  }
}

const markAsDelivered = (request: any) => {
  requestsToDeliver.value = [request]
  showDeliveredModal.value = true
}

const selectRequest = (request: any) => {
  selectedRequest.value = request
}

const openEditModal = (request: any) => {
  requestToEdit.value = request

  // Populate edit form
  editFormData.value = {
    student_name: request.student_name || '',
    school: request.school || '',
    equipment_type: request.equipment_type || '',
    status: request.status || '',
    needed_for: request.needed_for || [],
    minimum_rating: request.minimum_rating || 1,
    assignee: request.assignee || '',
    teacher_email: request.teacher_email || '',
    notes: request.notes || ''
  }

  showEditModal.value = true
}

const closeEditModal = () => {
  requestToEdit.value = null
  showEditModal.value = false
  editFormData.value = {
    student_name: '',
    school: '',
    equipment_type: '',
    status: '',
    needed_for: [],
    minimum_rating: 1,
    assignee: '',
    teacher_email: '',
    notes: ''
  }
}

const saveRequest = async () => {
  if (!requestToEdit.value) return

  // Validate form
  const { valid } = await editFormRef.value.validate()
  if (!valid) return

  submittingEdit.value = true

  try {
    await api.patch(`/requests/${requestToEdit.value.id}`, editFormData.value)

    await loadRequests()
    await loadStats()
    closeEditModal()
    showNotification('Request updated successfully', 'success')
  } catch (error: any) {
    console.error('Error updating request:', error)
    showNotification(error.response?.data?.detail || 'Failed to update request', 'error')
  } finally {
    submittingEdit.value = false
  }
}

const closeModal = () => {
  selectedRequest.value = null
}

const loadEquipmentForRequest = async () => {
  if (!requestToFill.value) return

  loadingEquipment.value = true

  try {
    // Get only available equipment
    const params: any = {
      equipment_type: requestToFill.value.equipment_type,
      status: 'available'
    }

    const response = await api.get('/equipment', params)

    if (response.data) {
      // Store all available equipment - the modal will handle sorting and matching
      availableEquipment.value = response.data
    }
  } catch (error) {
    console.error('Error loading equipment:', error)
    availableEquipment.value = []
  } finally {
    loadingEquipment.value = false
  }
}

const openFillModal = async (request: any) => {
  requestToFill.value = request
  showFillModal.value = true
  await loadEquipmentForRequest()
}

const closeFillModal = () => {
  showFillModal.value = false
  requestToFill.value = null
  availableEquipment.value = []
}

const openClaimModal = (equipment: any) => {
  equipmentToClaim.value = equipment
  showClaimModal.value = true
}

const closeClaimModal = () => {
  showClaimModal.value = false
  equipmentToClaim.value = null
}

const confirmClaim = async () => {
  if (!equipmentToClaim.value || !requestToFill.value) return

  try {
    // Claim equipment for the request
    await api.patch(`/equipment/${equipmentToClaim.value.id}/claim?claimed_for=${encodeURIComponent(requestToFill.value.id)}`)

    // Update request status to 'filled'
    await api.patch(`/requests/${requestToFill.value.id}/status`, {
      status: 'filled',
      assigned_equipment_id: equipmentToClaim.value.id
    })

    // Reload equipment list and close modal
    await loadEquipmentForRequest()
    closeClaimModal()
    closeFillModal()

    // Reload requests to show updated status
    await loadRequests()
    await loadStats()
    // Refresh badge count
    authStore.fetchSchoolRequestsCount()
    showNotification('Equipment claimed successfully', 'success')
  } catch (error: any) {
    console.error('Error claiming equipment:', error)

    // Check if equipment was already claimed
    if (error.response?.status === 400 && error.response?.data?.detail?.includes('not available')) {
      showNotification('This equipment has already been claimed by another user. Please refresh the page to see updated equipment.', 'warning')

      // Reload equipment list to show current state
      await loadEquipmentForRequest()
      closeClaimModal()
    } else {
      showNotification(error.response?.data?.detail || 'Failed to claim equipment', 'error')
    }
  }
}

const unclaimEquipment = async (equipment: any) => {
  try {
    await api.patch(`/equipment/${equipment.id}/unclaim`)

    // Reload equipment list
    await loadEquipmentForRequest()
    showNotification('Equipment unclaimed', 'success')
  } catch (error) {
    console.error('Error unclaiming equipment:', error)
    showNotification('Failed to unclaim equipment', 'error')
  }
}

const closeDeliveredModal = () => {
  showDeliveredModal.value = false
  requestsToDeliver.value = []
}

const confirmDelivered = async () => {
  if (!requestsToDeliver.value || requestsToDeliver.value.length === 0) return

  try {
    // Update each request status to 'completed'
    for (const request of requestsToDeliver.value) {
      await api.patch(`/requests/${request.id}/status`, {
        status: 'completed'
      })
    }

    await loadRequests()
    await loadStats()
    // Refresh badge count
    authStore.fetchSchoolRequestsCount()

    // Clear selected request if it was delivered
    if (selectedRequest.value && requestsToDeliver.value.some(r => r.id === selectedRequest.value.id)) {
      selectedRequest.value = null
    }

    // Clear selected items
    selectedRequests.value = []

    closeDeliveredModal()
    const count = requestsToDeliver.value.length
    showNotification(`${count} request${count > 1 ? 's' : ''} marked as delivered`, 'success')
  } catch (error) {
    console.error('Error marking request as delivered:', error)
    showNotification('Failed to mark request as delivered', 'error')
  }
}

const openDeleteModal = (request: any) => {
  requestToDelete.value = request
  showDeleteModal.value = true
}

const closeDeleteModal = () => {
  showDeleteModal.value = false
  requestToDelete.value = null
}

const confirmDelete = async () => {
  if (!requestToDelete.value) return

  deletingRequest.value = true

  try {
    await api.delete(`/requests/${requestToDelete.value.id}`)
    await loadRequests()
    await loadStats()
    // Refresh badge count
    authStore.fetchSchoolRequestsCount()
    if (selectedRequest.value && selectedRequest.value.id === requestToDelete.value.id) {
      selectedRequest.value = null
    }
    closeDeleteModal()
    showNotification('Request deleted successfully', 'success')
  } catch (error: any) {
    console.error('Error deleting request:', error)
    showNotification(error.response?.data?.detail || 'Failed to delete request', 'error')
  } finally {
    deletingRequest.value = false
  }
}

const bulkMarkDelivered = (requestIds: string[]) => {
  if (!requestIds || requestIds.length === 0) return

  // Get the full request objects from the ids
  const requestsToMark = requests.value.filter(r => requestIds.includes(r.id))
  requestsToDeliver.value = requestsToMark
  showDeliveredModal.value = true
}

const printDeliverySlips = async (requestIds: string[]) => {
  try {
    // Call the delivery slips API endpoint - it returns HTML
    const response = await api.post('/requests/delivery-slips', requestIds)

    // Open the HTML in a new window for printing
    const printWindow = window.open('', '_blank')
    if (printWindow) {
      printWindow.document.write(response.data)
      printWindow.document.close()
      // Optionally trigger print dialog
      setTimeout(() => {
        printWindow.focus()
        // printWindow.print() // Uncomment to auto-open print dialog
      }, 250)
    } else {
      showNotification('Please allow popups for this site to print delivery slips.', 'warning')
    }
  } catch (error: any) {
    console.error('Error generating delivery slips:', error)
    showNotification('Failed to generate delivery slips. Please try again.', 'error')
  }
}

const openBulkEditModal = (requestIds: string[]) => {
  bulkEditRequestIds.value = requestIds
  bulkEditError.value = ''
  showBulkEditModal.value = true
}

const closeBulkEditModal = () => {
  showBulkEditModal.value = false
  bulkEditRequestIds.value = []
  bulkEditError.value = ''
}

const confirmBulkEdit = async (updateData: any) => {
  if (!bulkEditRequestIds.value.length) {
    bulkEditError.value = 'No requests selected'
    return
  }

  // Check if any fields were filled in
  if (Object.keys(updateData).length === 0) {
    bulkEditError.value = 'Please fill in at least one field to update'
    return
  }

  bulkEditSubmitting.value = true
  bulkEditError.value = ''

  try {
    // Call bulk update API for each request
    for (const requestId of bulkEditRequestIds.value) {
      await api.patch(`/requests/${requestId}`, updateData)
    }

    await loadRequests()
    await loadStats()
    selectedRequests.value = []
    closeBulkEditModal()
    showNotification(`${bulkEditRequestIds.value.length} request(s) updated successfully`, 'success')
  } catch (error: any) {
    console.error('Error bulk updating requests:', error)
    if (error.response?.status === 403) {
      bulkEditError.value = 'You do not have permission to perform bulk updates.'
    } else {
      bulkEditError.value = error.response?.data?.detail || 'Failed to update requests. Please try again.'
    }
  } finally {
    bulkEditSubmitting.value = false
  }
}

const notifyTeacher = async () => {
  if (!editFormData.value.teacher_email || !requestToEdit.value) {
    showNotification('No teacher email to notify', 'error')
    return
  }

  sendingNotification.value = true

  try {
    // Get the assignee email from the current form selection
    let recipientEmail = editFormData.value.teacher_email

    // If an assignee is selected, find their email
    if (editFormData.value.assignee) {
      const selectedUser = users.value.find(u => u.id === editFormData.value.assignee)
      if (selectedUser && selectedUser.email) {
        recipientEmail = selectedUser.email
      }
    }

    await api.post(`/notifications/notify-teacher-about-request/${requestToEdit.value.id}`, {
      override_email: recipientEmail
    })
    showNotification(`Notification sent to ${recipientEmail}`, 'success')
  } catch (error: any) {
    console.error('Error sending notification:', error)
    showNotification(error.response?.data?.detail || 'Failed to send notification', 'error')
  } finally {
    sendingNotification.value = false
  }
}

onMounted(() => {
  loadRequests()
  loadStats()
  loadSchools()
  loadUsers()
  // Refresh the school requests count when entering requests view
  authStore.fetchSchoolRequestsCount()
})

return (_ctx: any,_cache: any) => {
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_textarea = _resolveComponent("v-textarea")!
  const _component_v_form = _resolveComponent("v-form")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_table = _resolveComponent("v-table")!
  const _component_v_snackbar = _resolveComponent("v-snackbar")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[46] || (_cache[46] = _createElementVNode("div", { class: "page-header" }, [
      _createElementVNode("h1", null, "Equipment Requests"),
      _createElementVNode("p", null, "Manage student equipment requests")
    ], -1)),
    _createVNode(RequestsFilters, {
      searchQuery: searchQuery.value,
      "onUpdate:searchQuery": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
      filterStatus: filterStatus.value,
      "onUpdate:filterStatus": _cache[1] || (_cache[1] = ($event: any) => ((filterStatus).value = $event)),
      filterSchool: filterSchool.value,
      "onUpdate:filterSchool": _cache[2] || (_cache[2] = ($event: any) => ((filterSchool).value = $event)),
      schoolOptions: schoolFilterOptions.value
    }, null, 8, ["searchQuery", "filterStatus", "filterSchool", "schoolOptions"]),
    _createVNode(RequestsTable, {
      requests: filteredRequests.value,
      loading: loading.value,
      schools: schools.value,
      isAdmin: isAdmin.value,
      "selected-items": selectedRequests.value,
      "onUpdate:selectedItems": _cache[3] || (_cache[3] = ($event: any) => (selectedRequests.value = $event)),
      onSelectRequest: selectRequest,
      onFill: openFillModal,
      onMarkDelivered: markAsDelivered,
      onEdit: openEditModal,
      onDelete: openDeleteModal,
      onBulkEdit: openBulkEditModal,
      onBulkMarkDelivered: bulkMarkDelivered,
      onPrintDeliverySlips: printDeliverySlips
    }, null, 8, ["requests", "loading", "schools", "isAdmin", "selected-items"]),
    _createVNode(RequestDetailsDialog, {
      request: selectedRequest.value,
      onClose: closeModal,
      onFillRequest: openFillModal,
      onMarkDelivered: markAsDelivered
    }, null, 8, ["request"]),
    _createVNode(EquipmentSelectionModal, {
      show: showFillModal.value,
      "onUpdate:show": _cache[4] || (_cache[4] = ($event: any) => ((showFillModal).value = $event)),
      request: requestToFill.value,
      equipment: availableEquipment.value,
      loading: loadingEquipment.value,
      schools: schools.value,
      onClaim: openClaimModal,
      onUnclaim: unclaimEquipment
    }, null, 8, ["show", "request", "equipment", "loading", "schools"]),
    _createVNode(_component_v_dialog, {
      modelValue: showEditModal.value,
      "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((showEditModal).value = $event)),
      "max-width": "800px"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_card, null, {
          default: _withCtx(() => [
            _createVNode(_component_v_card_title, null, {
              default: _withCtx(() => [...(_cache[20] || (_cache[20] = [
                _createElementVNode("h2", null, "Edit Request", -1)
              ]))]),
              _: 1
            }),
            _createVNode(_component_v_card_text, null, {
              default: _withCtx(() => [
                _createVNode(_component_v_form, {
                  ref_key: "editFormRef",
                  ref: editFormRef
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_row, null, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_col, {
                          cols: "12",
                          sm: "6"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_text_field, {
                              modelValue: editFormData.value.student_name,
                              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((editFormData.value.student_name) = $event)),
                              label: "Student Name",
                              variant: "outlined",
                              rules: [v => !!v || 'Student name is required'],
                              required: ""
                            }, null, 8, ["modelValue", "rules"])
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_v_col, {
                          cols: "12",
                          sm: "6"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_select, {
                              modelValue: editFormData.value.school,
                              "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((editFormData.value.school) = $event)),
                              items: schoolOptions.value,
                              label: "School",
                              variant: "outlined",
                              rules: [v => !!v || 'School is required'],
                              required: ""
                            }, null, 8, ["modelValue", "items", "rules"])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_v_row, null, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_col, {
                          cols: "12",
                          sm: "6"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_select, {
                              modelValue: editFormData.value.equipment_type,
                              "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((editFormData.value.equipment_type) = $event)),
                              items: equipmentTypeOptions,
                              label: "Equipment Type",
                              variant: "outlined",
                              rules: [v => !!v || 'Equipment type is required'],
                              required: ""
                            }, null, 8, ["modelValue", "rules"])
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_v_col, {
                          cols: "12",
                          sm: "6"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_select, {
                              modelValue: editFormData.value.status,
                              "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((editFormData.value.status) = $event)),
                              items: statusOptions,
                              label: "Status",
                              variant: "outlined",
                              rules: [v => !!v || 'Status is required'],
                              required: ""
                            }, null, 8, ["modelValue", "rules"])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_v_row, null, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_col, { cols: "12" }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_select, {
                              modelValue: editFormData.value.needed_for,
                              "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((editFormData.value.needed_for) = $event)),
                              items: taskOptions,
                              label: "Needed For (Tasks)",
                              variant: "outlined",
                              multiple: "",
                              chips: "",
                              "closable-chips": ""
                            }, null, 8, ["modelValue"])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_v_row, null, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_col, {
                          cols: "12",
                          sm: "6"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_text_field, {
                              modelValue: editFormData.value.minimum_rating,
                              "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((editFormData.value.minimum_rating) = $event)),
                              modelModifiers: { number: true },
                              label: "Minimum Rating",
                              type: "number",
                              min: "1",
                              max: "10",
                              variant: "outlined",
                              rules: [v => v >= 1 && v <= 10 || 'Rating must be between 1 and 10']
                            }, null, 8, ["modelValue", "rules"])
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_v_col, {
                          cols: "12",
                          sm: "6"
                        }, {
                          default: _withCtx(() => [
                            _createElementVNode("div", _hoisted_2, [
                              _createVNode(_component_v_select, {
                                modelValue: editFormData.value.assignee,
                                "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((editFormData.value.assignee) = $event)),
                                items: userOptions.value,
                                label: "Assignee (Optional)",
                                variant: "outlined",
                                hint: "Person responsible for fulfilling this request",
                                "persistent-hint": "",
                                clearable: "",
                                class: "flex-grow-1"
                              }, null, 8, ["modelValue", "items"]),
                              _createVNode(_component_v_btn, {
                                icon: "mdi-email-send",
                                color: "primary",
                                variant: "tonal",
                                size: "small",
                                disabled: !editFormData.value.teacher_email && !editFormData.value.assignee,
                                loading: sendingNotification.value,
                                onClick: notifyTeacher,
                                title: editFormData.value.assignee ? 'Send notification to selected assignee' : 'Send notification to teacher email'
                              }, null, 8, ["disabled", "loading", "title"])
                            ])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_v_row, null, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_col, { cols: "12" }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_text_field, {
                              modelValue: editFormData.value.teacher_email,
                              "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((editFormData.value.teacher_email) = $event)),
                              label: "Teacher's Email",
                              type: "email",
                              variant: "outlined",
                              placeholder: "teacher@school.edu"
                            }, null, 8, ["modelValue"])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_v_row, null, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_col, { cols: "12" }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_textarea, {
                              modelValue: editFormData.value.notes,
                              "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((editFormData.value.notes) = $event)),
                              label: "Notes",
                              variant: "outlined",
                              rows: "3",
                              placeholder: "Additional information or special requirements..."
                            }, null, 8, ["modelValue"])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 512)
              ]),
              _: 1
            }),
            _createVNode(_component_v_card_actions, null, {
              default: _withCtx(() => [
                _createVNode(_component_v_spacer),
                _createVNode(_component_v_btn, {
                  color: "grey",
                  onClick: closeEditModal
                }, {
                  default: _withCtx(() => [...(_cache[21] || (_cache[21] = [
                    _createTextVNode("Cancel", -1)
                  ]))]),
                  _: 1
                }),
                _createVNode(_component_v_btn, {
                  color: "primary",
                  onClick: saveRequest,
                  loading: submittingEdit.value
                }, {
                  default: _withCtx(() => [...(_cache[22] || (_cache[22] = [
                    _createTextVNode("Save", -1)
                  ]))]),
                  _: 1
                }, 8, ["loading"])
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(_component_v_dialog, {
      modelValue: showClaimModal.value,
      "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((showClaimModal).value = $event)),
      "max-width": "500px"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_card, null, {
          default: _withCtx(() => [
            _createVNode(_component_v_card_title, null, {
              default: _withCtx(() => [...(_cache[23] || (_cache[23] = [
                _createElementVNode("h2", null, "Claim Equipment", -1)
              ]))]),
              _: 1
            }),
            _createVNode(_component_v_card_text, null, {
              default: _withCtx(() => [
                _createElementVNode("p", _hoisted_3, [
                  _cache[24] || (_cache[24] = _createTextVNode(" Claim ", -1)),
                  _createElementVNode("strong", null, _toDisplayString(equipmentToClaim.value?.manufacturer) + " " + _toDisplayString(equipmentToClaim.value?.model), 1),
                  _cache[25] || (_cache[25] = _createTextVNode(" for ", -1)),
                  _createElementVNode("strong", null, _toDisplayString(requestToFill.value?.student_name), 1),
                  _cache[26] || (_cache[26] = _createTextVNode("? ", -1))
                ]),
                _createVNode(_component_v_alert, {
                  type: "info",
                  variant: "tonal"
                }, {
                  default: _withCtx(() => [...(_cache[27] || (_cache[27] = [
                    _createTextVNode(" This will mark the equipment as claimed for this request. ", -1)
                  ]))]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_component_v_card_actions, null, {
              default: _withCtx(() => [
                _createVNode(_component_v_spacer),
                _createVNode(_component_v_btn, {
                  color: "grey",
                  onClick: closeClaimModal
                }, {
                  default: _withCtx(() => [...(_cache[28] || (_cache[28] = [
                    _createTextVNode("Cancel", -1)
                  ]))]),
                  _: 1
                }),
                _createVNode(_component_v_btn, {
                  color: "success",
                  onClick: confirmClaim
                }, {
                  default: _withCtx(() => [...(_cache[29] || (_cache[29] = [
                    _createTextVNode("Claim for Request", -1)
                  ]))]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(_component_v_dialog, {
      modelValue: showDeliveredModal.value,
      "onUpdate:modelValue": _cache[16] || (_cache[16] = ($event: any) => ((showDeliveredModal).value = $event)),
      "max-width": requestsToDeliver.value.length > 1 ? '800px' : '500px'
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_card, null, {
          default: _withCtx(() => [
            _createVNode(_component_v_card_title, { class: "text-h5" }, {
              default: _withCtx(() => [...(_cache[30] || (_cache[30] = [
                _createTextVNode(" Confirm Delivery ", -1)
              ]))]),
              _: 1
            }),
            _createVNode(_component_v_card_text, null, {
              default: _withCtx(() => [
                (requestsToDeliver.value.length === 1)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                      _createElementVNode("p", _hoisted_5, [
                        _cache[31] || (_cache[31] = _createTextVNode(" Mark request for ", -1)),
                        _createElementVNode("strong", null, _toDisplayString(requestsToDeliver.value[0]?.student_name), 1),
                        _cache[32] || (_cache[32] = _createTextVNode(" as delivered? ", -1))
                      ]),
                      (requestsToDeliver.value[0]?.assigned_asset_tag)
                        ? (_openBlock(), _createBlock(_component_v_alert, {
                            key: 0,
                            type: "info",
                            variant: "tonal",
                            class: "mb-4"
                          }, {
                            default: _withCtx(() => [
                              _cache[33] || (_cache[33] = _createElementVNode("div", { class: "text-subtitle-2 mb-1" }, "Equipment Asset Tag:", -1)),
                              _createElementVNode("div", _hoisted_6, _toDisplayString(requestsToDeliver.value[0].assigned_asset_tag), 1)
                            ]),
                            _: 1
                          }))
                        : _createCommentVNode("", true)
                    ]))
                  : (_openBlock(), _createElementBlock("div", _hoisted_7, [
                      _createElementVNode("p", _hoisted_8, [
                        _cache[34] || (_cache[34] = _createTextVNode(" Mark ", -1)),
                        _createElementVNode("strong", null, _toDisplayString(requestsToDeliver.value.length), 1),
                        _cache[35] || (_cache[35] = _createTextVNode(" requests as delivered? ", -1))
                      ]),
                      _createVNode(_component_v_table, {
                        density: "compact",
                        class: "mb-4"
                      }, {
                        default: _withCtx(() => [
                          _cache[36] || (_cache[36] = _createElementVNode("thead", null, [
                            _createElementVNode("tr", null, [
                              _createElementVNode("th", null, "Student Name"),
                              _createElementVNode("th", null, "Asset Tag")
                            ])
                          ], -1)),
                          _createElementVNode("tbody", null, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(requestsToDeliver.value, (req) => {
                              return (_openBlock(), _createElementBlock("tr", {
                                key: req.id
                              }, [
                                _createElementVNode("td", null, _toDisplayString(req.student_name), 1),
                                _createElementVNode("td", null, [
                                  (req.assigned_asset_tag)
                                    ? (_openBlock(), _createBlock(_component_v_chip, {
                                        key: 0,
                                        size: "small",
                                        color: "primary"
                                      }, {
                                        default: _withCtx(() => [
                                          _createTextVNode(_toDisplayString(req.assigned_asset_tag), 1)
                                        ]),
                                        _: 2
                                      }, 1024))
                                    : (_openBlock(), _createElementBlock("span", _hoisted_9, "—"))
                                ])
                              ]))
                            }), 128))
                          ])
                        ]),
                        _: 1
                      })
                    ])),
                _createVNode(_component_v_alert, {
                  type: "success",
                  variant: "tonal"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(" This will update " + _toDisplayString(requestsToDeliver.value.length === 1 ? 'the request' : 'all requests') + " status to \"delivered\". ", 1)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_component_v_card_actions, null, {
              default: _withCtx(() => [
                _createVNode(_component_v_spacer),
                _createVNode(_component_v_btn, {
                  color: "grey",
                  onClick: closeDeliveredModal
                }, {
                  default: _withCtx(() => [...(_cache[37] || (_cache[37] = [
                    _createTextVNode("Cancel", -1)
                  ]))]),
                  _: 1
                }),
                _createVNode(_component_v_btn, {
                  color: "success",
                  onClick: confirmDelivered
                }, {
                  default: _withCtx(() => [...(_cache[38] || (_cache[38] = [
                    _createTextVNode("Mark as Delivered", -1)
                  ]))]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue", "max-width"]),
    _createVNode(_component_v_dialog, {
      modelValue: showDeleteModal.value,
      "onUpdate:modelValue": _cache[17] || (_cache[17] = ($event: any) => ((showDeleteModal).value = $event)),
      "max-width": "500px"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_card, null, {
          default: _withCtx(() => [
            _createVNode(_component_v_card_title, { class: "text-h5" }, {
              default: _withCtx(() => [...(_cache[39] || (_cache[39] = [
                _createTextVNode(" Confirm Delete ", -1)
              ]))]),
              _: 1
            }),
            _createVNode(_component_v_card_text, null, {
              default: _withCtx(() => [
                _createElementVNode("p", _hoisted_10, [
                  _cache[40] || (_cache[40] = _createTextVNode(" Are you sure you want to delete the request for ", -1)),
                  _createElementVNode("strong", null, _toDisplayString(requestToDelete.value?.student_name), 1),
                  _cache[41] || (_cache[41] = _createTextVNode("? ", -1))
                ]),
                _createVNode(_component_v_alert, {
                  type: "warning",
                  variant: "tonal"
                }, {
                  default: _withCtx(() => [...(_cache[42] || (_cache[42] = [
                    _createTextVNode(" This action cannot be undone. ", -1)
                  ]))]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_component_v_card_actions, null, {
              default: _withCtx(() => [
                _createVNode(_component_v_spacer),
                _createVNode(_component_v_btn, {
                  color: "grey",
                  onClick: closeDeleteModal
                }, {
                  default: _withCtx(() => [...(_cache[43] || (_cache[43] = [
                    _createTextVNode("Cancel", -1)
                  ]))]),
                  _: 1
                }),
                _createVNode(_component_v_btn, {
                  color: "error",
                  onClick: confirmDelete,
                  loading: deletingRequest.value
                }, {
                  default: _withCtx(() => [...(_cache[44] || (_cache[44] = [
                    _createTextVNode("Delete", -1)
                  ]))]),
                  _: 1
                }, 8, ["loading"])
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(BulkEditRequestModal, {
      show: showBulkEditModal.value,
      schools: schools.value,
      "item-count": bulkEditRequestIds.value.length,
      submitting: bulkEditSubmitting.value,
      error: bulkEditError.value,
      onClose: closeBulkEditModal,
      onSubmit: confirmBulkEdit
    }, null, 8, ["show", "schools", "item-count", "submitting", "error"]),
    _createVNode(_component_v_snackbar, {
      modelValue: showSnackbar.value,
      "onUpdate:modelValue": _cache[19] || (_cache[19] = ($event: any) => ((showSnackbar).value = $event)),
      color: snackbarColor.value,
      timeout: 3000,
      location: "top"
    }, {
      actions: _withCtx(() => [
        _createVNode(_component_v_btn, {
          variant: "text",
          onClick: _cache[18] || (_cache[18] = ($event: any) => (showSnackbar.value = false))
        }, {
          default: _withCtx(() => [...(_cache[45] || (_cache[45] = [
            _createTextVNode(" Close ", -1)
          ]))]),
          _: 1
        })
      ]),
      default: _withCtx(() => [
        _createTextVNode(_toDisplayString(snackbarMessage.value) + " ", 1)
      ]),
      _: 1
    }, 8, ["modelValue", "color"])
  ]))
}
}

})